/**
 * @module validation/password
 *
 * Backend rules found in:
 * Itslearning\ItslCore\BusinessLayer\SecurityPolicy.cs
 *
 * Should be kept in sync
 */

/**
 * Validate character length of string
 *
 * @param {string} password - The password value
 * @param {string} complexity - The set password complexity. Options are either "low", "medium" or "strong". Default is "strong"
 * @param {number} low - Number of characters for low complexity. Default is 7
 * @param {number} medium - Number of characters for medium complexity. Default is 10
 * @param {number} strong - Number of characters for strong complexity. Default is 13
 * @returns {boolean} true if password length meets character length requirement
 */
export function validateCharacterLength(
  password,
  complexity = "strong",
  low = 7,
  medium = 10,
  strong = 13
) {
  if (password === undefined || password.length === 0) {
    return false;
  }

  let minimumLength = strong;

  if (complexity === "low") {
    minimumLength = low;
  } else if (complexity === "medium") {
    minimumLength = medium;
  }

  return password.length >= minimumLength;
}

/**
 * Validate if string contains lowercase characters
 *
 * @param {string} password - The password value
 * @returns {boolean} true if password contains any lowercase characters
 */
export function validateLowercase(password) {
  if (typeof password === "undefined") {
    return false;
  }

  const lowercaseRegexp = /[a-z]/;

  return lowercaseRegexp.test(password);
}

/**
 * Validate if string contains uppercase characters
 *
 * @param {string} password - The password value
 * @returns {boolean} true if password contains any uppercase characters
 */
export function validateUppercase(password) {
  const uppercaseRegexp = /[A-Z]/;

  return uppercaseRegexp.test(password);
}

/**
 * Validate whether string contains the name of the user or their username
 *
 * @param {object} userIdentity - Object containing properties: username, firstName and lastName
 * @param {string} password - The password value
 * @returns {boolean} true if password does include neither name or username value
 */
export function validateNoUserIdentity(userIdentity, password) {
  if (userIdentity === undefined || userIdentity.length === 0) {
    return false;
  }

  const lcPassword = password.toLowerCase();

  if (
    (userIdentity.firstName !== "" &&
    lcPassword.includes(userIdentity.firstName.toLowerCase())) ||
    (userIdentity.lastName !== "" &&
    lcPassword.includes(userIdentity.lastName.toLowerCase())) ||
    (userIdentity.username !== "" &&
    lcPassword.includes(userIdentity.username.toLowerCase()))
  ) {
    return false;
  }

  return true;
}

/**
 * Validate if string contains number characters
 *
 * @param {string} password - The password value
 * @returns {boolean} true if password contains any number characters
 */
export function validateNumbers(password) {
  const numberRegexp = /[0-9]/;

  return numberRegexp.test(password);
}

/**
 * Validate if string contains symbol characters
 *
 * @param {string} password - The password value
 * @returns {boolean} true if password contains any number characters
 */
export function validateSymbols(password) {
  const symbolRegexp = /[^a-zA-Z0-9]/;

  return symbolRegexp.test(password);
}
